import { installResizeCompatibility } from "./resize";
import registry from "virtual:compat-styles";
import "./styles.css";

type Query = { id: string; selector: string; name: string; operator: string; width: number; media: string[] };
type Container = { selector: string; name: string; media: string[] };
const active = (media: string[]) => media.every(query => window.matchMedia(query).matches);
export function matchesContainerWidth(width: number, operator: string, limit: number) {
  return operator === "<" ? width < limit : operator === "<=" ? width <= limit : operator === ">" ? width > limit : width >= limit;
}
let installed = false;
/** Discover newly mounted component containers, never rescan the page on polling or input. */
export function installDomCompatibility() {
  if (installed) return () => {};
  installed = true;
  const root = document.documentElement;
  document.body.classList.add("compat-root");
  const supports = (property: string, value?: string) => {
    try { return value ? CSS.supports(property, value) : CSS.supports(property); } catch { return false; }
  };
  const legacy = !supports("selector(:has(*))") || !supports("container-type", "inline-size") || !supports("height", "100dvh") || !supports("selector(:focus-visible)");
  root.classList.toggle("compat-legacy-layout", legacy);
  root.classList.toggle("compat-no-individual-transform", !supports("translate", "1px"));
  if (!legacy) return () => { installed = false; };
  installResizeCompatibility();
  const updateViewport = () => root.style.setProperty("--compat-vh", `${(window.visualViewport?.height ?? window.innerHeight) / 100}px`);
  updateViewport();
  window.addEventListener("resize", updateViewport);
  window.addEventListener("orientationchange", updateViewport);
  window.visualViewport?.addEventListener("resize", updateViewport);

  const containers = new Map<Element, Container[]>();
  const widths = new Map<Element, number>();
  const targets = new Map<Element, Query[]>();
  const containerSelectors = registry.containers.map(item => item.selector).join(",");
  const targetSelectors = registry.queries.map(item => item.selector).join(",");
  function closest(target: Element, query: Query) {
    for (let node = target.parentElement; node; node = node.parentElement) {
      if (containers.get(node)?.some(item => active(item.media) && (!query.name || item.name.split(/\s+/).includes(query.name)))) return node;
    }
    return undefined;
  }
  const measure = (node: Element) => {
    const style = getComputedStyle(node);
    return Math.max(0, node.getBoundingClientRect().width - parseFloat(style.paddingLeft || "0") - parseFloat(style.paddingRight || "0") - parseFloat(style.borderLeftWidth || "0") - parseFloat(style.borderRightWidth || "0"));
  };
  function update(target: Element, queries: Query[]) {
    for (const query of queries) {
      const container = closest(target, query);
      const match = !!container && active(query.media) && matchesContainerWidth(widths.get(container) ?? measure(container), query.operator, query.width);
      target.toggleAttribute(`data-compat-cq-${query.id}`, match);
    }
  }
  const resized = new Set<Element>();
  let resizeFrame: number | undefined;
  const resize = new ResizeObserver(entries => {
    for (const entry of entries) { widths.set(entry.target, entry.contentRect.width); resized.add(entry.target); }
    if (resizeFrame === undefined) resizeFrame = requestAnimationFrame(() => {
      resizeFrame = undefined;
      for (const [target, queries] of targets) if (queries.some(query => { const parent = closest(target, query); return parent && resized.has(parent); })) update(target, queries);
      resized.clear();
    });
  });
  function register(node: Element) {
    const matches = registry.containers.filter(item => node.matches(item.selector));
    if (matches.length) {
      if (!containers.has(node)) resize.observe(node);
      containers.set(node, matches); widths.set(node, measure(node));
    } else if (containers.delete(node)) { widths.delete(node); resize.unobserve(node); }
    const queries = registry.queries.filter(item => node.matches(item.selector));
    const previous = targets.get(node) ?? [];
    for (const query of previous) if (!queries.some(item => item.id === query.id)) node.removeAttribute(`data-compat-cq-${query.id}`);
    if (queries.length) { targets.set(node, queries); update(node, queries); } else targets.delete(node);
  }
  function discover(node: Element) {
    register(node);
    if (containerSelectors) node.querySelectorAll(containerSelectors).forEach(register);
    if (targetSelectors) node.querySelectorAll(targetSelectors).forEach(register);
  }
  // The initial mount is the only whole-document inventory. Subsequent work is scoped to changed components.
  discover(document.body);
  const mutation = new MutationObserver(records => {
    const changed = new Set<Element>();
    for (const record of records) {
      if (record.target instanceof Element) {
        if (record.type === "attributes") changed.add(record.target);
        else register(record.target);
      }
      record.addedNodes.forEach(node => { if (node instanceof Element) changed.add(node); });
    }
    for (const node of changed) if (node.isConnected && ![...changed].some(parent => parent !== node && parent.contains(node))) discover(node);
    for (const [node] of containers) if (!node.isConnected) { resize.unobserve(node); containers.delete(node); widths.delete(node); }
    for (const [node] of targets) if (!node.isConnected) targets.delete(node);
    if (records.some(record => record.removedNodes.length)) for (const [target, queries] of targets) update(target, queries);
  });
  mutation.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["class", "data-report-type", "hidden"] });
  const updateMedia = () => {
    for (const node of containers.keys()) widths.set(node, measure(node));
    for (const [target, queries] of targets) update(target, queries);
  };
  window.addEventListener("resize", updateMedia);
  return () => {
    mutation.disconnect(); resize.disconnect(); containers.clear(); targets.clear(); widths.clear(); installed = false;
    if (resizeFrame !== undefined) cancelAnimationFrame(resizeFrame);
    window.removeEventListener("resize", updateMedia);
    window.removeEventListener("resize", updateViewport);
    window.removeEventListener("orientationchange", updateViewport);
    window.visualViewport?.removeEventListener("resize", updateViewport);
  };
}
